import { SingleValue } from "react-select";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import Card from "../../../../../components/layouts/card/card";
import {
	EXCHANGE_BALANCING,
	EXCHANGE_BALANCING_MAP,
	EXCHANGE_DISCOUNT_TYPE,
	EXCHANGE_DISCOUNT_TYPE_MAP,
	PRICE_CALCULATIONS,
	PRICE_CALCULATIONS_MAP,
	PRICE_CHANGE_MAP,
	PRICE_CHANGE_SETTING,
	SEARCH_PRODUCT_BY_ARR,
	SEARCH_PRODUCT_BY_FIELDS
} from "../../../../../models/User";
import { FEATURES } from "../../../../../models/Plan";
import currentPortalStore from "../../../../../stores/current-portal-store";
import { FUNC } from "../../../../../stores/platform-service-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import { Toggle } from "../../../../../components/inputs/toggle/toggle";
import ControlGroup from "../../../../../components/layouts/control-group/control-group";
import { observer } from "mobx-react";
import { Note, NOTE_TYPE } from "../../../../../components/note/note";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { Input } from "../../../../../components/inputs/input/input";
import Hr from "../../../../../components/hr/hr";
import shopifySettingsStore from '../../../../integrations/products-services/stores/shopify-settings-store';
import ExchangeLocationsSection
	from "../../exchange-locations/components/exchange-locations-section/exchange-locations-section";
import SettingStore from "../../../../../stores/setting-store";
import ExchangeLocationsSettingsInfoSection
	from "../../exchange-locations/components/exchange-locations-settings-info-section/exchange-locations-settings-info-section";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";

interface IProps {
	onExpandClick: (expanded: boolean) => void;
	isExpanded: boolean;
	dataName?: string; /** used by Candu to target an element */
}

const PriceDifferenceCalculationsCard: React.FC<IProps> = observer(({onExpandClick, isExpanded, dataName}) => {
	const {getTranslation} = useDashboardTranslation();
	const pageScope = 'dashboard-settings-store';

	const platform = currentPortalStore.platform;
	const storeSetting = currentPortalStore.setting;
	const supportsLineItemDiscount = platform?.isFunctionalitySupported(FUNC.FUNC_LINE_ITEM_DISCOUNT_FOR_DRAFT_ORDER);

	const exchangeBalancingOptions = [
		{
			label: getTranslation(EXCHANGE_BALANCING_MAP[EXCHANGE_BALANCING.DISCOUNT_APPLIED].label, pageScope),
			value: EXCHANGE_BALANCING.DISCOUNT_APPLIED
		},
		{
			label: getTranslation(EXCHANGE_BALANCING_MAP[EXCHANGE_BALANCING.GIFT_CARD_PAYMENT].label, pageScope),
			value: EXCHANGE_BALANCING.GIFT_CARD_PAYMENT
		},
	];

	const exchangeBalancingValue = storeSetting?.exchangeBalancing ? {
		value: storeSetting.exchangeBalancing,
		label: getTranslation(EXCHANGE_BALANCING_MAP[storeSetting.exchangeBalancing].label, pageScope)
	} : undefined;


	const exchangePriceOptions = [
		{
			label: getTranslation(PRICE_CHANGE_MAP[PRICE_CHANGE_SETTING.FOLLOW_THE_ORIGINAL_REQUEST].label, pageScope),
			value: PRICE_CHANGE_SETTING.FOLLOW_THE_ORIGINAL_REQUEST,
			description: getTranslation(PRICE_CHANGE_MAP[PRICE_CHANGE_SETTING.FOLLOW_THE_ORIGINAL_REQUEST].description, pageScope),
		},
		{
			label: getTranslation(PRICE_CHANGE_MAP[PRICE_CHANGE_SETTING.OVERRIDE_THE_ORIGINAL_REQUEST].label, pageScope),
			value: PRICE_CHANGE_SETTING.OVERRIDE_THE_ORIGINAL_REQUEST,
			description: getTranslation(PRICE_CHANGE_MAP[PRICE_CHANGE_SETTING.OVERRIDE_THE_ORIGINAL_REQUEST].description, pageScope),
		},
	];

	const productExchangePriceChangeValue = storeSetting?.productExchangePriceChange ? {
		value: storeSetting.productExchangePriceChange,
		label: getTranslation(PRICE_CHANGE_MAP[storeSetting.productExchangePriceChange].label, pageScope),
		description: getTranslation(PRICE_CHANGE_MAP[storeSetting.productExchangePriceChange].label, pageScope)
	} : undefined;

	const variantExchangePriceChangeValue = storeSetting?.variantExchangePriceChange ? {
		value: storeSetting.variantExchangePriceChange,
		label: getTranslation(PRICE_CHANGE_MAP[storeSetting.variantExchangePriceChange].label, pageScope),
		description: getTranslation(PRICE_CHANGE_MAP[storeSetting.variantExchangePriceChange].label, pageScope)
	} : undefined;

	const onChangeExchangeBalancing = (value: SingleValue<IOption>) => {
		storeSetting?.setExchangeBalancing(value?.value as EXCHANGE_BALANCING);
	}

	const onChangeProductExchangePriceChange = (value: SingleValue<IOption>) => {
		storeSetting?.setProductExchangePriceChange(value?.value as PRICE_CHANGE_SETTING);
	}

	const onChangeVariantExchangePriceChange = (value: SingleValue<IOption>) => {
		storeSetting?.setVariantExchangePriceChange(value?.value as PRICE_CHANGE_SETTING);
	}

	const exchangeDiscountTypeOptions = [
		{
			label: getTranslation(EXCHANGE_DISCOUNT_TYPE_MAP[EXCHANGE_DISCOUNT_TYPE.LINE_ITEM_DISCOUNT].label, pageScope),
			value: EXCHANGE_DISCOUNT_TYPE.LINE_ITEM_DISCOUNT
		},
		{
			label: getTranslation(EXCHANGE_DISCOUNT_TYPE_MAP[EXCHANGE_DISCOUNT_TYPE.ORDER_LEVEL_DISCOUNT].label, pageScope),
			value: EXCHANGE_DISCOUNT_TYPE.ORDER_LEVEL_DISCOUNT
		},
	];

	const exchangeDiscountTypeValue = storeSetting?.exchangeBalancing ? {
		value: storeSetting.exchangeBalancing,
		label: getTranslation(EXCHANGE_DISCOUNT_TYPE_MAP[storeSetting.exchangeDiscountType].label, pageScope)
	} : undefined;

	const onChangeExchangeDiscountType = (value: SingleValue<IOption>) => {
		storeSetting?.setExchangeDiscountType(value?.value as EXCHANGE_DISCOUNT_TYPE);
	}

	const options = [
		{
			label: getTranslation(PRICE_CALCULATIONS_MAP[PRICE_CALCULATIONS.PAID_PRICE].label, pageScope),
			value: PRICE_CALCULATIONS.PAID_PRICE
		},
		{
			label: getTranslation(PRICE_CALCULATIONS_MAP[PRICE_CALCULATIONS.CATALOG_PRICE].label, pageScope),
			value: PRICE_CALCULATIONS.CATALOG_PRICE
		},
		{
			label: getTranslation(PRICE_CALCULATIONS_MAP[PRICE_CALCULATIONS.CATALOG_PRICE_TIME_OF_PURCHASE].label, pageScope),
			value: PRICE_CALCULATIONS.CATALOG_PRICE_TIME_OF_PURCHASE
		},
	];

	const calculatePriceDiffByValue = storeSetting?.calculatePriceDiffBy ? {
		value: storeSetting.calculatePriceDiffBy,
		label: getTranslation(PRICE_CALCULATIONS_MAP[storeSetting.calculatePriceDiffBy].label, pageScope)
	} : undefined;

	const onChangeCalculatePriceDiffBy = (value: SingleValue<IOption>) => {
		storeSetting?.setCalculatePriceDiffBy(value?.value as PRICE_CALCULATIONS);
	}

	const calculateProductExchangePriceDiffByValue = storeSetting?.calculateProductExchangePriceDiffBy ? {
		value: storeSetting.calculateProductExchangePriceDiffBy,
		label: getTranslation(PRICE_CALCULATIONS_MAP[storeSetting.calculateProductExchangePriceDiffBy].label, pageScope)
	} : undefined;

	const onChangeCalculateProductExchangePriceDiffBy = (value: SingleValue<IOption>) => {
		storeSetting?.setCalculateProductExchangePriceDiffBy(value?.value as PRICE_CALCULATIONS);
	}

	const searchProductByValid = storeSetting?.searchBy.isValid ?? false;

	const onChangeSearchProductBy = (keyChanged: SEARCH_PRODUCT_BY_FIELDS, value: boolean) => {
		storeSetting?.searchBy.setValue(keyChanged, value);
	}

	const onChangeIsBrowseByCollectionActive = (value: boolean) => {
		storeSetting?.browseByCollection.setIsBrowseByCollectionActive(value);
	}

	const onChangeCollectionTitles = (value: string) => {
		storeSetting?.browseByCollection.setCollectionTitles(value);
	}

	return <Card
		toolbar={{
			title: (
				<FontBody1 variant={FONT_VARIANT.BOLD}>
					<DashTranslation translationKey="PRICE_DIFFERENCE_CALCULATION" scope={pageScope}/>
				</FontBody1>
			)
		}}
		expandCallback={onExpandClick}
		isExpanded={isExpanded}
		dataName={dataName}
	>
		<FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
			{getTranslation('EXCHANGES_GENERAL', pageScope)}
		</FontBody1>
		<FormFieldLine
			label={getTranslation('ALLOW_REFUND_FOR_EXCHANGED_ITEMS', pageScope)}
			note={getTranslation('ENABLE_TO_ALLOW_REFUNDS_FOR_EXCHANGED_ITEMS', pageScope)}
			dataName="allow-refund-for-exchanged-items"
		>
			<Toggle
				checked={storeSetting?.allowRefundOfExchanged}
				onChange={storeSetting?.setAllowRefundOfExchanged}
				label=''
			/>
		</FormFieldLine>

		{currentPortalStore.isFeatureAllowed(FEATURES.BETA_EXCHANGE_OF_EXCHANGED_ITEMS) && storeSetting && (
			<FormFieldLine
				label={getTranslation('ALLOW_EXCHANGES_FOR_EXCHANGED_ITEMS', pageScope)}
				note={getTranslation('ENABLE_TO_ALLOW_EXCHANGES_FOR_EXCHANGED_ITEMS', pageScope)}
				dataName="allow-exchanges-for-exchanged-items"
			>
				<Toggle
					checked={storeSetting.allowExchangeOfExchanged}
					onChange={storeSetting.setAllowExchangeOfExchanged}
					label=''
				/>
			</FormFieldLine>
		)}

		{/* Legacy toggle - visible only during migration period for merchants who still have it enabled.
		    Will be removed once all merchants are migrated to Custom Inventory Rules (ET-1669). */}
		{!!storeSetting?.fulfillExchangesBasedOnOrderCountry && (
			<FormFieldLine
				label={getTranslation('FULFILL_EXCHANGES_BASED_ON_ORDER_COUNTRY', pageScope)}
				note={getTranslation('ENABLE_TO_EXCHANGES_BE_FULFILLED_FROM_SAME_WAREHOUSE_AS_COUNTRY', pageScope)}
				dataName="fulfill-exchanges-based-on-order-country"
			>
				<Toggle
					checked={storeSetting?.fulfillExchangesBasedOnOrderCountry}
					onChange={storeSetting?.setFulfillExchangesBasedOnOrderCountry}
					label=''
				/>
			</FormFieldLine>
		)}

		<ResolveCapability capability={PROFILE_CAPABILITIES.CUSTOM_INVENTORY}>
			{({ isSupported }) =>
				isSupported && storeSetting && (
					<>
						<ExchangeLocationsSettingsInfoSection/>
						<FormFieldLine
							label={getTranslation('CUSTOM_INVENTORY_RULES', pageScope)}
							note={getTranslation('CUSTOM_INVENTORY_RULES_NOTE', pageScope)}
						>
							<ExchangeLocationsSection
								settingStore={storeSetting as SettingStore}
							/>
						</FormFieldLine>
					</>
				)}
		</ResolveCapability>

		<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
			{getTranslation('EXCHANGE_DRAFT_ORDER_SETTINGS', pageScope)}
		</FontBody2>

		<FormFieldLine
			label={getTranslation('EXCHANGE_BALANCING', pageScope)}
			note={getTranslation('SET_HOW_TO_DEDUCT_PAID_AMOUNT', pageScope)}
			dataName="exchange-balancing"
		>
			<SelectDropdown
				options={exchangeBalancingOptions}
				value={exchangeBalancingValue}
				onChange={onChangeExchangeBalancing}
				disabled={true}
			/>
		</FormFieldLine>

		{((exchangeBalancingValue?.value === EXCHANGE_BALANCING.DISCOUNT_APPLIED) && supportsLineItemDiscount) && (
			<FormFieldLine
				label={getTranslation('EXCHANGE_DISCOUNT_TYPE', pageScope)}
				note={getTranslation('SET_HOW_TO_APPLY_DISCOUNT', pageScope)}
				dataName="exchange-discount-type"
			>
				<SelectDropdown
					options={exchangeDiscountTypeOptions}
					value={exchangeDiscountTypeValue}
					onChange={onChangeExchangeDiscountType}
				/>
			</FormFieldLine>
		)}
		<ControlGroup>
			<Note
				type={NOTE_TYPE.INFORMATION}
				body={getTranslation('TO_IGNORE_EXCHANGE_DISCOUNT_FILTER_REASON', pageScope)}
			/>
        </ControlGroup>

		<ResolveCapability capability={PROFILE_CAPABILITIES.VARIANT_EXCHANGE}>
			{({ isSupported }) => isSupported && (
				<>
					<Hr/>

					<FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
						{getTranslation('VARIANT_EXCHANGE', pageScope)}
					</FontBody1>

					<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
						{getTranslation('PRICE_DIFFERENCE_CALCULATIONS', pageScope)}
					</FontBody2>

					<FormFieldLine
						label={getTranslation('VARIANT_EXCHANGE_PRICE_SOURCE', pageScope)}
						note={getTranslation('WHEN_PAYED_PRICE_CUSTOMERS_PAY_FULL_PRICE_IF_SAME_VARIANT', pageScope)}
						dataName="variant-exchange-price-source"
					>
						<SelectDropdown
							options={options}
							value={calculatePriceDiffByValue}
							onChange={onChangeCalculatePriceDiffBy}
						/>
					</FormFieldLine>

					<FormFieldLine
						label={getTranslation('CHANGES_TO_NEW_ITEMS_PRICE', pageScope)}
						note={getTranslation('CHANGES_TO_NEW_ITEMS_PRICE_DESCRIPTION', pageScope)}
						dataName="changes-to-new-items-price-variant"
					>
						<SelectDropdown
							options={exchangePriceOptions}
							value={variantExchangePriceChangeValue}
							onChange={onChangeVariantExchangePriceChange}
						/>
					</FormFieldLine>
				</>
			)}
		</ResolveCapability>

		<ResolveCapability capability={PROFILE_CAPABILITIES.PRODUCT_EXCHANGE}>
			{({ isSupported, isFeatureEnabled, isProductSearchSupported, isSearchByCollectionSupported, isShopNowSupported }) => {
				if (!isSupported) {
					return null;
				}
				return (
					<>
						<Hr/>

						<FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
							{getTranslation('PRODUCT_EXCHANGE', pageScope)}
						</FontBody1>


						{isProductSearchSupported && (
							<>
								<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
									{getTranslation('FIND_PRODUCTS', pageScope)}
								</FontBody2>

								<FormFieldLine
									label={getTranslation('SEARCH_PRODUCTS_BY', pageScope)}
									note={getTranslation('FILTERING_MULTI_FIELDS_AFFECTS_SPEED', pageScope)}
									dataName="search-products-by"
								>
									<ControlGroup>{
										SEARCH_PRODUCT_BY_ARR.map((field: { label: string, key: SEARCH_PRODUCT_BY_FIELDS }) => {
											return (
												<Toggle
													key={`toggleSearchField${field.key}`}
													checked={storeSetting?.searchBy.getValue(field.key)}
													onChange={(v: boolean) => onChangeSearchProductBy(field.key, v)}
													label={getTranslation(field.label, pageScope)}
												/>
											)
										})
									}</ControlGroup>
								</FormFieldLine>

								{
									// Error message for SEARCH_PRODUCTS_BY
									!searchProductByValid && (
										<FormFieldLine label=''>
											<Note
												body={getTranslation('ATLEAST_ONE_SEARCH_FIELD_REQUIRED', pageScope)}
												type={NOTE_TYPE.ERROR}
											>
											</Note>
										</FormFieldLine>
									)
								}

								{isSearchByCollectionSupported && (
									<>
										<FormFieldLine
											label={getTranslation('BROWSE_BY_COLLECTION', pageScope)}
											note={getTranslation('BROWSE_BY_COLLECTION_NOTE', pageScope)}
											dataName="browse-by-collection"
										>
											<Toggle
												checked={!!storeSetting?.isBrowseByCollectionActive}
												onChange={onChangeIsBrowseByCollectionActive}
												label=''
											/>
										</FormFieldLine>

										{!!storeSetting?.isBrowseByCollectionActive &&
											<FormFieldLine
												label={getTranslation('COLLECTION_INPUT', pageScope)}
												note={getTranslation('COLLECTION_INPUT_NOTE', pageScope)}
												dataName="collection-input"
											>
												<Input
													value={storeSetting?.browseByCollection.collectionTitles}
													onChange={onChangeCollectionTitles}
												/>
											</FormFieldLine>
										}

										{

											// Error message for BROWSE_BY_COLLECTION
											storeSetting?.browseByCollection.invalidTitles && (
												<FormFieldLine label=''>
													<Note
														body={getTranslation('BROWSE_BY_COLLECTION_ERROR', pageScope, [storeSetting?.browseByCollection.invalidTitles])}
														type={NOTE_TYPE.ERROR}
													>
													</Note>
												</FormFieldLine>
											)
										}
									</>
								)}
							</>
						)}

						{isFeatureEnabled && (
							<>
								<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
									{getTranslation('PRICE_DIFFERENCE_CALCULATIONS', pageScope)}
								</FontBody2>

								<FormFieldLine
									label={getTranslation('PRODUCT_EXCHANGE_PRICE_SOURCE', pageScope)}
									note={getTranslation('WHEN_PAYED_PRICE_CUSTOMERS_PAY_FULL_PRICE_IF_SAME_CATALOG_PRICE', pageScope)}
									dataName="product-exchange-price-source"
								>
									<SelectDropdown
										options={options}
										value={calculateProductExchangePriceDiffByValue}
										onChange={onChangeCalculateProductExchangePriceDiffBy}
									/>
								</FormFieldLine>

								<FormFieldLine
									label={getTranslation('CHANGES_TO_NEW_ITEMS_PRICE', pageScope)}
									note={getTranslation('CHANGES_TO_NEW_ITEMS_PRICE_DESCRIPTION', pageScope)}
									dataName="changes-to-new-items-price-product"
								>
									<SelectDropdown
										options={exchangePriceOptions}
										value={productExchangePriceChangeValue}
										onChange={onChangeProductExchangePriceChange}
									/>
								</FormFieldLine>

								{isShopNowSupported && (
									<>
										<Hr/>

										<FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
											{getTranslation('SHOP_NOW', pageScope)}
										</FontBody1>

										<FormFieldLine
											label={getTranslation('SHOP_NOW_LABEL', pageScope)}
											note={
												<DashTranslation
													translationKey="SHOP_NOW_DESCRIPTION"
													scope={pageScope}
												/>
											}
											dataName="shop-now-label"
										>
											<Toggle
												checked={storeSetting?.anyToAnyExchangesStore?.enableShopNow}
												onChange={storeSetting?.anyToAnyExchangesStore?.setEnableShopNow}
												label=''
											/>
										</FormFieldLine>

										<FontBody2 color={FONT_COLOR.NEUTRAL3}>
											<DashTranslation
												translationKey="SHOP_NOW_LINK"
												params={[`${shopifySettingsStore.returnPortalUrl}?exchangeInStore=1`]}
												scope={pageScope}
											/>
										</FontBody2>
									</>
								)}
							</>
						)}
					</>
				);
			}}
		</ResolveCapability>


	</Card>
});

export default PriceDifferenceCalculationsCard;